<script>
    import { Switch, Overlay, ProgressCircular, Snackbar } from "svelte-materialify";
    import { onMount } from "svelte";

    export let gxid = null; // id_galaxy of the product being edited

    let discounts = [];
    let priceLists = [];
    let loading = false;
    let saving = false;
    let dialogOpen = false;
    let snackbar = false;
    let snackbarMsg = "";

    let editId = null;
    let selectedGxCode = "";
    let discountType = "percent";
    let discountValue = "";
    let startOf = "";
    let endOf = "";
    let notes = "";

    const typeOptions = [
        { text: "Ποσοστό (%)", value: "percent" },
        { text: "Καρφωτή τιμή (€)", value: "fixed_price" },
    ];

    async function fetchDiscounts() {
        if (!gxid) return;
        loading = true;
        try {
            const res = await fetch(
                `/api/v1/shop/catalog-discounts/product/${encodeURIComponent(gxid)}`,
                { credentials: "include" }
            );
            discounts = res.ok ? await res.json() : [];
        } catch (e) {
            discounts = [];
        }
        loading = false;
    }

    async function fetchPriceLists() {
        try {
            const res = await fetch(`/api/v1/shop/catalog-discounts/pricelists`, {
                credentials: "include",
            });
            priceLists = res.ok ? await res.json() : [];
        } catch (e) {
            priceLists = [];
        }
    }

    onMount(() => {
        fetchDiscounts();
        fetchPriceLists();
    });

    // re-fetch if the tab is opened for a different product without remounting
    let lastGxid = gxid;
    $: if (gxid && gxid !== lastGxid) {
        lastGxid = gxid;
        fetchDiscounts();
    }

    function resetForm() {
        editId = null;
        selectedGxCode = "";
        discountType = "percent";
        discountValue = "";
        startOf = "";
        endOf = "";
        notes = "";
    }

    function openAdd() {
        resetForm();
        dialogOpen = true;
    }

    function toDatetimeLocal(v) {
        if (!v) return "";
        return String(v).replace(" ", "T").slice(0, 16);
    }

    function openEdit(row) {
        editId = row.id;
        selectedGxCode = row.gx_code;
        discountType = row.discount_type;
        discountValue = row.discount_value;
        startOf = toDatetimeLocal(row.start_of);
        endOf = toDatetimeLocal(row.end_of);
        notes = row.notes || "";
        dialogOpen = true;
    }

    function closeDialog() {
        dialogOpen = false;
        resetForm();
    }

    function showError(msg) {
        snackbarMsg = msg;
        snackbar = true;
    }

    async function saveDiscount() {
        if (!selectedGxCode) {
            showError("Επιλέξτε τιμοκατάλογο.");
            return;
        }
        if (discountValue === "" || Number.isNaN(Number(discountValue))) {
            showError("Συμπληρώστε έγκυρη τιμή έκπτωσης.");
            return;
        }
        saving = true;
        try {
            const payload = {
                discount_type: discountType,
                discount_value: Number(discountValue),
                start_of: startOf || null,
                end_of: endOf || null,
                notes: notes || null,
            };
            let res;
            if (editId) {
                res = await fetch(`/api/v1/shop/catalog-discounts/${editId}`, {
                    method: "PATCH",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
            } else {
                res = await fetch(`/api/v1/shop/catalog-discounts`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ...payload,
                        gx_code: selectedGxCode,
                        id_galaxy: gxid,
                    }),
                });
            }
            if (!res.ok) throw new Error();
            closeDialog();
            await fetchDiscounts();
        } catch (e) {
            showError("Σφάλμα κατά την αποθήκευση.");
        }
        saving = false;
    }

    async function toggleActive(row) {
        try {
            await fetch(`/api/v1/shop/catalog-discounts/${row.id}`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ active: row.active ? 0 : 1 }),
            });
            await fetchDiscounts();
        } catch (e) {
            showError("Σφάλμα ενημέρωσης.");
        }
    }

    async function deleteDiscount(id) {
        if (!confirm("Διαγραφή αυτής της έκπτωσης τιμοκαταλόγου;")) return;
        try {
            await fetch(`/api/v1/shop/catalog-discounts/${id}`, {
                method: "DELETE",
                credentials: "include",
            });
            await fetchDiscounts();
        } catch (e) {
            showError("Σφάλμα διαγραφής.");
        }
    }

    function isCurrentlyActive(row) {
        if (!row.active) return false;
        const now = new Date();
        if (row.start_of && new Date(row.start_of) > now) return false;
        if (row.end_of && new Date(row.end_of) < now) return false;
        return true;
    }
</script>

<div class="relative">
    <Overlay absolute active={saving}>
        <ProgressCircular color="white" indeterminate size={64} />
    </Overlay>

    <div class="flex items-center justify-between mb-3">
        <div class="text-lg font-semibold text-slate-700">
            Εκπτώσεις ανά τιμοκατάλογο
        </div>
        <button
            on:click={openAdd}
            disabled={!gxid}
            class="bg-green-600 rounded-md text-white text-sm font-semibold px-3 py-1.5 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300"
        >
            + Προσθήκη έκπτωσης
        </button>
    </div>
    <div class="text-xs text-gray-400 mb-3">
        Η έκπτωση εφαρμόζεται αυτόματα σε <b>όλους</b> τους πελάτες που έχουν αυτόν
        τον τιμοκατάλογο (Catalog) στο προφίλ τους. Αν ένας συγκεκριμένος πελάτης έχει
        ξεχωριστή ειδική τιμή (tab "Εκπτώσεις" στη σελίδα του), εκείνη υπερισχύει.
    </div>

    {#if loading}
        <div class="flex justify-center py-6">
            <ProgressCircular indeterminate color="primary" />
        </div>
    {:else if !discounts.length}
        <div class="text-slate-400 text-sm py-4">
            Δεν έχουν οριστεί εκπτώσεις τιμοκαταλόγου για αυτό το προϊόν.
        </div>
    {:else}
        <div class="overflow-x-auto">
            <table class="w-full table-auto text-sm">
                <thead>
                    <tr class="bg-gray-200 text-gray-600 text-left">
                        <th class="py-2 px-2">Τιμοκατάλογος</th>
                        <th class="py-2 px-2">Τύπος</th>
                        <th class="py-2 px-2">Τιμή</th>
                        <th class="py-2 px-2">Από</th>
                        <th class="py-2 px-2">Έως</th>
                        <th class="py-2 px-2 text-center">Ενεργή</th>
                        <th class="py-2 px-2 text-center">Ενέργειες</th>
                    </tr>
                </thead>
                <tbody>
                    {#each discounts as row (row.id)}
                        <tr class="border-b border-gray-200 hover:bg-gray-50">
                            <td class="py-2 px-2 font-medium">{row.gx_code}</td>
                            <td class="py-2 px-2">
                                {row.discount_type === "fixed_price"
                                    ? "Καρφωτή τιμή"
                                    : "Ποσοστό"}
                            </td>
                            <td class="py-2 px-2">
                                {row.discount_type === "fixed_price"
                                    ? `${Number(row.discount_value).toFixed(2)}€`
                                    : `${Number(row.discount_value).toFixed(0)}%`}
                            </td>
                            <td class="py-2 px-2">
                                {row.start_of ? new Date(row.start_of).toLocaleString("el-GR") : "-"}
                            </td>
                            <td class="py-2 px-2">
                                {row.end_of ? new Date(row.end_of).toLocaleString("el-GR") : "-"}
                            </td>
                            <td class="py-2 px-2 text-center">
                                <div class="flex flex-col items-center">
                                    <Switch
                                        checked={!!row.active}
                                        on:change={() => toggleActive(row)}
                                    />
                                    {#if row.active && !isCurrentlyActive(row)}
                                        <span class="text-xs text-orange-500"
                                            >Εκτός περιόδου</span
                                        >
                                    {/if}
                                </div>
                            </td>
                            <td class="py-2 px-2 text-center">
                                <button
                                    on:click={() => openEdit(row)}
                                    class="bg-indigo-500 text-white text-xs rounded px-2 py-1 mr-1 hover:bg-indigo-600"
                                >
                                    Επεξ.
                                </button>
                                <button
                                    on:click={() => deleteDiscount(row.id)}
                                    class="bg-red-500 text-white text-xs rounded px-2 py-1 hover:bg-red-600"
                                >
                                    Διαγρ.
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>

{#if dialogOpen}
    <div
        class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
        <div class="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <div class="text-xl font-semibold mb-4">
                {editId ? "Επεξεργασία έκπτωσης τιμοκαταλόγου" : "Νέα έκπτωση τιμοκαταλόγου"}
            </div>

            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Τιμοκατάλογος (Catalog code)</label
                >
                {#if editId}
                    <div
                        class="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-600"
                    >
                        {selectedGxCode}
                    </div>
                {:else if priceLists.length}
                    <select
                        bind:value={selectedGxCode}
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    >
                        <option value="">-- επιλέξτε --</option>
                        {#each priceLists as code}
                            <option value={code}>{code}</option>
                        {/each}
                    </select>
                {:else}
                    <input
                        type="text"
                        bind:value={selectedGxCode}
                        placeholder="π.χ. 0047"
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    />
                {/if}
            </div>

            <div class="mb-4 grid grid-cols-2 gap-3">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                        >Τύπος έκπτωσης</label
                    >
                    <select
                        bind:value={discountType}
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    >
                        {#each typeOptions as opt}
                            <option value={opt.value}>{opt.text}</option>
                        {/each}
                    </select>
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        {discountType === "fixed_price"
                            ? "Τιμή (€)"
                            : "Ποσοστό έκπτωσης (%)"}
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        bind:value={discountValue}
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    />
                </div>
            </div>

            <div class="mb-4 grid grid-cols-2 gap-3">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                        >Ισχύει από (προαιρετικό)</label
                    >
                    <input
                        type="datetime-local"
                        bind:value={startOf}
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                        >Ισχύει έως (προαιρετικό)</label
                    >
                    <input
                        type="datetime-local"
                        bind:value={endOf}
                        class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    />
                </div>
            </div>

            <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Σημειώσεις (προαιρετικό)</label
                >
                <input
                    type="text"
                    bind:value={notes}
                    class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                />
            </div>

            <div class="flex justify-end gap-2">
                <button
                    type="button"
                    on:click={closeDialog}
                    class="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                    Άκυρο
                </button>
                <button
                    type="button"
                    on:click={saveDiscount}
                    class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                    Αποθήκευση
                </button>
            </div>
        </div>
    </div>
{/if}

<Snackbar class="flex-column red text-white" bind:active={snackbar} bottom center timeout={3000}>
    {snackbarMsg}
</Snackbar>
